import classNames from "classnames";
import CiroTable from "../../shared/CiroTable/CiroTable";
import CiroTableHeader, {
  CiroTableHeaderAlignEnum,
} from "../../shared/CiroTable/CiroTableHeader";
import CiroTableRow from "../../shared/CiroTable/CiroTableRow";
import { gql, useLazyQuery } from "@apollo/client";
import {
  AutopilotSessionContactRow_AutopilotCuratedSearchFragmentDoc,
  AutopilotSessionContactRow_ContactExtraDetailsFragment,
  AutopilotSessionContactRow_ContactFragment,
  AutopilotSessionContactRow_ContactFragmentDoc,
  AutopilotSessionContactTable_AutopilotSessionFragment,
  AutopilotSessionContactTable_ContactExtraDetailsQuery,
  AutopilotSessionContactTable_ContactExtraDetailsQueryVariables,
  AutopilotSessionRunningStateStatus,
} from "../../../__generated__/graphql";
import { useContext, useEffect, useMemo, useState } from "react";
import CiroDropdownCheckboxButton from "../../shared/CiroTableHeaderCheckbox/CiroDropdownCheckboxButton";
import {
  useFragment as getFragmentData,
  useFragment,
} from "../../../__generated__";
import AutopilotSessionContactRow, {
  AutopilotSessionContactRow_AutopilotCuratedSearch,
  AutopilotSessionContactRow_Contact,
  AutopilotSessionContactRow_ContactExtraDetails,
} from "./AutopilotSessionContactRow";
import { IAutopilotContactDetailsFilters } from "../../../reactHooks/filters/contactList/useAutopilotUrlQueryParams";
import NoContactsFound from "../../automationList/AutomationListHomeComponents/NoContactsFound";
import AutopilotContactTableHeaderSortButton from "../shared/AutopilotContactTableHeaderSortButton";
import NavigationContext from "../../../contexts/NavigationContext";

export const AutopilotSessionContactTable_AutopilotSession = gql`
  fragment AutopilotSessionContactTable_AutopilotSession on AutopilotSession {
    id
    source
    contacts {
      id
      ...AutopilotSessionContactRow_Contact
      org_contact {
        id
      }
    }
    curatedSearch {
      ...AutopilotSessionContactRow_AutopilotCuratedSearch
      scoringCriteria {
        id
        label
        criterion
      }
    }
    runningState {
      status
    }
  }
  ${AutopilotSessionContactRow_AutopilotCuratedSearch}
  ${AutopilotSessionContactRow_Contact}
`;

const AutopilotSessionContactTable_ContactExtraDetails = gql`
  query AutopilotSessionContactTable_ContactExtraDetails($ids: [Int!]!) {
    autopilotSessionSavedContacts(ids: $ids) {
      id
      ...AutopilotSessionContactRow_ContactExtraDetails
    }
  }
  ${AutopilotSessionContactRow_ContactExtraDetails}
`;

export const CONTACTS_MAX_NUM_ROWS = 25;

interface IAutopilotSessionContactTableProps {
  autopilotSession?: AutopilotSessionContactTable_AutopilotSessionFragment | null;
  filters: IAutopilotContactDetailsFilters;
  filteredContactIds: number[];
  selectedContactIdsToOrgContactIds: Map<number, number>;
  setSelectedContactIdsToOrgContactIds: (ids: Map<number, number>) => void;
}

const AutopilotSessionContactTable = ({
  autopilotSession,
  filters,
  filteredContactIds,
  selectedContactIdsToOrgContactIds,
  setSelectedContactIdsToOrgContactIds,
}: IAutopilotSessionContactTableProps) => {
  const [offset, setOffset] = useState(1);
  const offsetStart = useMemo(
    () => (offset - 1) * CONTACTS_MAX_NUM_ROWS,
    [offset],
  );
  const offsetEnd = useMemo(() => offset * CONTACTS_MAX_NUM_ROWS, [offset]);
  const offsetContactIdString = useMemo(
    // Put into a string so that we can use it as a dependency in useMemo
    () => JSON.stringify(filteredContactIds.slice(offsetStart, offsetEnd)),
    [filteredContactIds, offsetStart, offsetEnd],
  );
  const offsetContactIds: number[] = useMemo(
    () => JSON.parse(offsetContactIdString),
    [offsetContactIdString],
  );
  const [extraDetailsForContactIds, setExtraDetailsForContactIds] = useState<
    Map<number, AutopilotSessionContactRow_ContactExtraDetailsFragment>
  >(new Map());

  const [getExtraDetails, { loading: extraDetailsLoading }] = useLazyQuery<
    AutopilotSessionContactTable_ContactExtraDetailsQuery,
    AutopilotSessionContactTable_ContactExtraDetailsQueryVariables
  >(AutopilotSessionContactTable_ContactExtraDetails);

  useEffect(() => {
    const unprocessedIds = offsetContactIds.filter(
      (id) => !extraDetailsForContactIds.has(id),
    );
    if (unprocessedIds.length > 0) {
      getExtraDetails({
        variables: { ids: unprocessedIds },
        onCompleted: (data) => {
          setExtraDetailsForContactIds((prev) => {
            const newEntries = data.autopilotSessionSavedContacts.map(
              (contact) =>
                [
                  contact.id,
                  contact as AutopilotSessionContactRow_ContactExtraDetailsFragment,
                ] as const,
            );
            return new Map<
              number,
              AutopilotSessionContactRow_ContactExtraDetailsFragment
            >([...prev, ...newEntries]);
          });
        },
      });
    }
  }, [offsetContactIds, getExtraDetails, extraDetailsForContactIds]);

  const { isNavCollapsed, refreshNavData } = useContext(NavigationContext);

  const contactMap = useMemo(
    () =>
      new Map(
        autopilotSession?.contacts?.map((contact) => [contact.id, contact]) ||
          [],
      ),
    [autopilotSession?.contacts],
  );

  const filteredContacts = useMemo(
    () =>
      autopilotSession?.contacts?.filter((contact) =>
        filteredContactIds.includes(contact.id),
      ) ?? [],
    [autopilotSession?.contacts, filteredContactIds],
  );

  useEffect(() => {
    if (filteredContacts.length > 0) {
      refreshNavData();
    }
  }, [filteredContacts.length, refreshNavData]);

  // Reset pagination when filtered contacts change
  useEffect(() => {
    setOffset(1);
  }, [filteredContactIds]);

  const handleSelectAll = () => {
    if (selectedContactIdsToOrgContactIds.size === filteredContacts.length) {
      setSelectedContactIdsToOrgContactIds(new Map());
    } else {
      setSelectedContactIdsToOrgContactIds(
        new Map(
          filteredContacts.map((contact) => [
            contact.id,
            contact.org_contact.id!,
          ]),
        ),
      );
    }
  };

  const toggleSelectVisible = () => {
    const newSelectedIds = new Map(selectedContactIdsToOrgContactIds);
    // Check if all visible contacts are selected
    const allVisibleSelected = offsetContactIds.every((contactId) =>
      selectedContactIdsToOrgContactIds.has(contactId),
    );

    offsetContactIds.forEach((contactId) => {
      const contact = contactMap.get(contactId);
      if (contact) {
        if (allVisibleSelected) {
          // If all visible are selected, unselect them
          newSelectedIds.delete(contact.id);
        } else {
          // If not all visible are selected, select them
          newSelectedIds.set(contact.id, contact.org_contact.id ?? 0);
        }
      }
    });
    setSelectedContactIdsToOrgContactIds(newSelectedIds);
  };

  const handleUnselectVisible = () => {
    const newSelectedIds = new Map(selectedContactIdsToOrgContactIds);
    offsetContactIds.forEach((contactId) => {
      newSelectedIds.delete(contactId);
    });
    setSelectedContactIdsToOrgContactIds(newSelectedIds);
  };

  const handleSelectContact = (
    contact: AutopilotSessionContactRow_ContactFragment,
  ) => {
    const newSelectedIds = new Map(selectedContactIdsToOrgContactIds);
    if (selectedContactIdsToOrgContactIds.has(contact.id)) {
      newSelectedIds.delete(contact.id);
    } else {
      newSelectedIds.set(contact.id, contact.org_contact.id ?? 0);
    }
    setSelectedContactIdsToOrgContactIds(newSelectedIds);
  };

  const curatedSearch = useFragment(
    AutopilotSessionContactRow_AutopilotCuratedSearchFragmentDoc,
    autopilotSession?.curatedSearch,
  );

  const noContactsFoundMessage = [
    AutopilotSessionRunningStateStatus.Completed,
    AutopilotSessionRunningStateStatus.Paused,
  ].includes(
    autopilotSession?.runningState?.status ??
      AutopilotSessionRunningStateStatus.Loading,
  )
    ? "Try adjusting your search or filters."
    : "Still searching...";

  return (
    <div className={classNames("pt-2", "w-full")}>
      {filteredContacts.length === 0 ? (
        <NoContactsFound subtitle={noContactsFoundMessage} />
      ) : (
        <>
          <CiroTable
            scrollable={true}
            pagination={{
              currentPage: offset,
              totalPages: Math.ceil(
                filteredContacts.length / CONTACTS_MAX_NUM_ROWS,
              ),
              onPageChange: (page: number) => setOffset(page),
            }}
          >
            <thead className={classNames("table-header-group")}>
              <CiroTableRow clickable={false}>
                <CiroTableHeader isFirst={true} width="w-24">
                  <CiroDropdownCheckboxButton
                    canSelectRecordsInBulk={true}
                    canSelectMoreRecords={
                      selectedContactIdsToOrgContactIds.size <
                      filteredContacts.length
                    }
                    numDisplayedRecords={filteredContacts.length}
                    handleTopCheckboxSelection={toggleSelectVisible}
                    noDropdownActions={false}
                    noSelectionLimit={true}
                    numberVisibleChecked={
                      selectedContactIdsToOrgContactIds.size
                    }
                    selectFirstMaxNumberRecords={handleSelectAll}
                    startIdx={0}
                    totalCount={filteredContacts.length}
                    totalNumberChecked={selectedContactIdsToOrgContactIds.size}
                    unselectAll={() => {
                      setSelectedContactIdsToOrgContactIds(new Map());
                    }}
                    unselectVisible={handleUnselectVisible}
                  />
                </CiroTableHeader>
                <CiroTableHeader>
                  <AutopilotContactTableHeaderSortButton
                    filters={filters}
                    columnName="name"
                    columnDisplay="Name"
                  />
                </CiroTableHeader>
                <CiroTableHeader>
                  <AutopilotContactTableHeaderSortButton
                    filters={filters}
                    columnName="companyName"
                    columnDisplay="Company"
                  />
                </CiroTableHeader>
                <CiroTableHeader align={CiroTableHeaderAlignEnum.CENTER}>
                  <AutopilotContactTableHeaderSortButton
                    filters={filters}
                    columnName="criteriaMatched"
                    columnDisplay="Criteria Matched"
                  />
                </CiroTableHeader>
                {autopilotSession?.curatedSearch?.scoringCriteria.map(
                  (criterion) => (
                    <CiroTableHeader
                      key={criterion.id}
                      align={CiroTableHeaderAlignEnum.CENTER}
                      className={classNames(
                        isNavCollapsed ? "max-xl:hidden" : "max-2xl:hidden",
                      )}
                    >
                      <AutopilotContactTableHeaderSortButton
                        filters={filters}
                        columnName={`scoringCriteria_${criterion.id}`}
                        columnDisplay={criterion.label}
                      />
                    </CiroTableHeader>
                  ),
                )}

                <CiroTableHeader isLast={true}>Contact</CiroTableHeader>
              </CiroTableRow>
            </thead>
            <tbody className={classNames("table-row-group")}>
              {offsetContactIds.map((contactId) => {
                const contact = contactMap.get(contactId);
                if (!contact) {
                  return null;
                }
                const contactFragment = getFragmentData(
                  AutopilotSessionContactRow_ContactFragmentDoc,
                  contact,
                );
                return (
                  <AutopilotSessionContactRow
                    key={contact.id}
                    curatedSearch={curatedSearch}
                    autopilotSessionId={autopilotSession?.id ?? null}
                    contact={contactFragment}
                    extraDetails={
                      extraDetailsForContactIds.get(contact.id) ?? null
                    }
                    extraDetailsLoading={
                      !extraDetailsForContactIds.has(contact.id) &&
                      extraDetailsLoading
                    }
                    isLastTwoRows={
                      contact.id ===
                        offsetContactIds[offsetContactIds.length - 1] ||
                      contact.id ===
                        offsetContactIds[offsetContactIds.length - 2]
                    }
                    isSelected={selectedContactIdsToOrgContactIds.has(
                      contact.id,
                    )}
                    onSelect={() => handleSelectContact(contactFragment)}
                  />
                );
              })}
            </tbody>
          </CiroTable>
        </>
      )}
    </div>
  );
};

export default AutopilotSessionContactTable;
